import React from 'react'
import { ClipboardCopy } from '@components/ClipboardCopy/ClipboardCopy'
import { NodeOperatorIcon } from '@components/NodeOperatorIcon/NodeOperatorIcon'
import { OperatorYield } from '@components/Web3/Wallet/StakedTokensTab'
import { Box, Grid, Paragraph, Stack } from '@ui'
import { StakableOperator } from 'hooks/useOperatorData'
import { pillShimmerClass } from 'ui/styles/globals/shimmer.css'
import { shortAddress } from 'ui/utils/utils'

// Utility functions for formatting metrics
const formatHttpLatency = (latencyMs: number): string => {
    return `${latencyMs}ms`
}

const formatUptimePercentage = (percentage: number): string => {
    return `${percentage.toFixed(1)}%`
}

export const NodeOperatorCard = ({
    operator,
    operatorAddress,
}: {
    operator: StakableOperator
    operatorAddress: `0x${string}`
}) => {
    if (!operator) {
        return null
    }

    return (
        <Box border gap padding="lg" background="level2" rounded="md">
            <Stack horizontal gap="paragraph">
                <Box>
                    <NodeOperatorIcon nodeOperator={operator} />
                </Box>
                <Box justifyContent="spaceBetween" paddingY="xs">
                    <Paragraph strong size="lg" color="default">
                        {operator.name}
                    </Paragraph>
                    <Stack horizontal gap="xs">
                        <ClipboardCopy
                            label={shortAddress(operatorAddress)}
                            clipboardContent={operatorAddress}
                        />
                    </Stack>
                </Box>
            </Stack>
            <NodeStatus operator={operator} />
        </Box>
    )
}

const NodeStatus = ({ operator }: { operator: StakableOperator }) => {
    return (
        <Grid columns={2}>
            <Box gap="sm">
                <Paragraph color="gray1">Health</Paragraph>
                <Stack horizontal gap="sm" grow={false}>
                    <Box centerContent>
                        <Box shrink width="x1" height="x1" background="positive" rounded="full" />
                    </Box>
                    <Paragraph color="gray2">
                        {formatHttpLatency(operator.metrics.http20)}
                    </Paragraph>
                </Stack>
            </Box>
            <Box gap="sm">
                <Paragraph color="gray1">Uptime</Paragraph>
                <Paragraph color="gray2">
                    {formatUptimePercentage(operator.metrics.uptime_percentage)}
                </Paragraph>
            </Box>
            <Box gap="sm">
                <Paragraph color="gray1">Est. Yield</Paragraph>
                <Paragraph color="gray2">
                    <OperatorYield operatorAddress={operator.address} />
                </Paragraph>
            </Box>
            <Box gap="sm">
                <Paragraph color="gray1">Commission</Paragraph>
                <Paragraph color="gray2">{operator.commissionPercentage}%</Paragraph>
            </Box>
        </Grid>
    )
}

export const NodeOperatorSkeletonCard = () => {
    return (
        <Box border gap padding="lg" background="level2" rounded="md">
            <Stack horizontal gap="paragraph">
                <Box>
                    <NodeOperatorIcon nodeOperator={undefined} />
                </Box>
                <Box justifyContent="spaceBetween" paddingY="xs" width="100%">
                    <Box height="x2" width="50%" className={pillShimmerClass} rounded="xs" />
                    <Box height="x2" width="50%" className={pillShimmerClass} rounded="xs" />
                </Box>
            </Stack>
            <Stack gap="sm">
                <Box height="x4" width="100%" className={pillShimmerClass} rounded="xs" />
            </Stack>
        </Box>
    )
}
